import { useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { forgetPushOnSignOut } from "../utils/push";
import { acceptTerms } from "../api/me";
import { useAuth } from "../context/AuthContext";
import { buttonPrimaryCls, buttonSecondaryCls, errorBannerCls } from "../styles/ui";
import logo from "../assets/logo.png";

/**
 * Full-screen, unskippable gate shown after sign-in until the person has
 * accepted the STINTE STACK confidentiality/acceptable-use notice at least
 * once. Rendered by App.jsx's PrivateRoute instead of the app shell whenever
 * `me.terms_accepted_at` is falsy — see markTermsAccepted in AuthContext for
 * how acceptance clears it without a full page reload.
 *
 * Text is the notice verbatim (as supplied), not a paraphrase — this is the
 * actual agreement being clicked through, so its wording isn't ours to edit.
 */
export default function TermsGate() {
  const { markTermsAccepted } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleAgree() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await acceptTerms();
      markTermsAccepted(res.terms_accepted_at);
    } catch (err) {
      setError("Could not record your acceptance. Please try again.");
      setSubmitting(false);
    }
  }

  async function handleDecline() {
    try {
      await forgetPushOnSignOut();
      await signOut(getAuth());
    } finally {
      window.location.assign("/");
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center bg-surface-muted px-6 py-10 overflow-y-auto">
      <div className="w-full max-w-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-600 flex items-center justify-center mb-4 shadow-md">
            <img
              src={logo}
              alt="STINTE"
              className="h-8 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement.innerHTML = '<span class="text-white text-xl font-bold">S</span>';
              }}
            />
          </div>
          <h1 className="text-xl font-semibold text-ink text-center">
            STINTE STACK — Confidentiality and Acceptable Use Notice
          </h1>
        </div>

        <div className="bg-surface border border-line rounded-xl shadow-sm px-6 py-6 text-sm text-ink leading-relaxed space-y-4">
          <p>
            This system and the information contained within it are the confidential
            and proprietary property of STINTE LLC (&ldquo;STINTE,&rdquo; &ldquo;the
            Company&rdquo;). Access is restricted to authorized users only.
          </p>
          <p>By selecting &ldquo;I Agree&rdquo; below, you acknowledge and agree to the following:</p>

          <div>
            <p className="font-semibold text-ink mb-1">1. Confidentiality</p>
            <p>
              Any data, documents, client information, project details, pricing, or
              business processes accessed through this system are confidential. You
              agree not to disclose, copy, distribute, or share this information with
              any third party without prior written authorization from STINTE LLC.
            </p>
          </div>

          <div>
            <p className="font-semibold text-ink mb-1">2. Authorized Use Only</p>
            <p>
              Access to this system is granted for legitimate business purposes
              related to your role at STINTE LLC. Unauthorized access, use, or
              distribution of information contained herein may result in disciplinary
              action, termination, and/or legal action.
            </p>
          </div>

          <div>
            <p className="font-semibold text-ink mb-1">3. Client Data Protection</p>
            <p>
              Certain information within this system relates to STINTE clients,
              including but not limited to Costco and other partners. This
              information is subject to client confidentiality obligations and must
              be handled accordingly.
            </p>
          </div>

          <div>
            <p className="font-semibold text-ink mb-1">4. Monitoring</p>
            <p>
              Use of this system may be monitored and logged for security and
              compliance purposes. Continued use constitutes consent to such
              monitoring.
            </p>
          </div>

          <div>
            <p className="font-semibold text-ink mb-1">5. No Unauthorized Copying or Export</p>
            <p>
              Users may not export, download, print, or otherwise remove data from
              this system except as required for authorized business functions.
            </p>
          </div>

          <div>
            <p className="font-semibold text-ink mb-1">6. Acknowledgment</p>
            <p>
              By clicking &ldquo;I Agree,&rdquo; you confirm that you have read,
              understood, and agree to be bound by this notice, and that you
              understand a violation of these terms may result in disciplinary or
              legal consequences.
            </p>
          </div>
        </div>

        {error && <p className={`${errorBannerCls} mt-4`}>{error}</p>}

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6">
          <button
            type="button"
            className={`${buttonSecondaryCls} sm:flex-none sm:px-6`}
            onClick={handleDecline}
            disabled={submitting}
          >
            Decline and Exit
          </button>
          <button
            type="button"
            className={`${buttonPrimaryCls} sm:flex-none sm:px-6`}
            onClick={handleAgree}
            disabled={submitting}
          >
            {submitting ? "Recording…" : "I Agree"}
          </button>
        </div>
      </div>
    </div>
  );
}
